import { VENMO_AUTOMATION_STAGE_STATUSES } from './venmoAutomationStageStatuses'
import * as venmoUrls from './venmoUrls'
import { VenmoSettingKey } from '../../recommendations/recommendationSettingsKeys'
import * as enums from '../../utils/enums'
import { VenmoRecommendationKey } from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import {
  getCurrentValueForCheckbox,
  saveCurrentValueForCheckbox,
  toggleCheckbox,
} from '../checkboxDetection'
import { wait } from '../interruptiblePromises'
import {
  defaultCantUndoFn,
  getCurrentValueFnFromKey,
  getErrorIsDisabledAndReasonFn,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'

const AUTOMATIC_FRIENDING_SETTINGS = {
  AUTOMATIC_FRIENDING_PHONE_CONTACTS: 'Phone contacts',
}

// Settings properties
const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) => (value ? 'On' : 'Off'))

const getIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(key, (value) => !value)

const getCantUndoFn = () => defaultCantUndoFn

function getIsDisabledFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabled
}
function getIsDisabledReasonFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabledReason
}

// Setup gather stage definitions

enum StageKey {
  GATHER_SETTING_AUTOMATIC_FRIENDING = 'GATHER_SETTING_AUTOMATIC_FRIENDING',
  UPDATE_SETTING_AUTOMATIC_FRIENDING_PHONE_CONTACTS = 'UPDATE_SETTING_AUTOMATIC_FRIENDING_PHONE_CONTACTS',
  UNDO_SETTING_AUTOMATIC_FRIENDING_PHONE_CONTACTS = 'UNDO_SETTING_AUTOMATIC_FRIENDING_PHONE_CONTACTS',
}

const gatherStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_AUTOMATIC_FRIENDING,
    status: VENMO_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: venmoUrls.friendsAndSocialUrl,
    action: async (machineContext, userContext) => {
      await saveCurrentValueForCheckbox(
        userContext.updateStoredAutomationErrors,
        userContext.updateStoredInitialValues,
        AUTOMATIC_FRIENDING_SETTINGS,
        VenmoSettingKey.AUTOMATIC_FRIENDING_PHONE_CONTACTS,
        VenmoRecommendationKey.VENMO_AUTOMATIC_FRIENDING,
      )
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_AUTOMATIC_FRIENDING_PHONE_CONTACTS,
    status: VENMO_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: venmoUrls.friendsAndSocialUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, isDry } = userContext
      // Skip if not selected
      if (
        !settingsSelected ||
        !settingsSelected[VenmoSettingKey.AUTOMATIC_FRIENDING_PHONE_CONTACTS]
      ) {
        return null
      }

      // Check phone setting
      const currentCheckbox = await getCurrentValueForCheckbox(
        AUTOMATIC_FRIENDING_SETTINGS.AUTOMATIC_FRIENDING_PHONE_CONTACTS,
      )

      // Toggle setting if checkbox true
      if (currentCheckbox && !isDry) {
        await toggleCheckbox(
          AUTOMATIC_FRIENDING_SETTINGS.AUTOMATIC_FRIENDING_PHONE_CONTACTS,
        )
        // If we navigate away from the page too quickly, change won't save;
        // Couldn't figure out how to get a MutationObserver promise to help us
        await wait(1000)
      }
      return null
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_AUTOMATIC_FRIENDING_PHONE_CONTACTS,
    status: VENMO_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: venmoUrls.friendsAndSocialUrl,
    action: async (machineContext, userContext) => {
      const { initialValues } = userContext

      // Check phone setting
      const currentSetting = await getCurrentValueForCheckbox(
        AUTOMATIC_FRIENDING_SETTINGS.AUTOMATIC_FRIENDING_PHONE_CONTACTS,
      )

      // Toggle setting if checkbox does not match initial values
      if (
        initialValues?.[VenmoSettingKey.AUTOMATIC_FRIENDING_PHONE_CONTACTS] !==
        currentSetting
      ) {
        await toggleCheckbox(
          AUTOMATIC_FRIENDING_SETTINGS.AUTOMATIC_FRIENDING_PHONE_CONTACTS,
        )
        // If we navigate away from the page too quickly, change won't save;
        // Couldn't figure out how to get a MutationObserver promise to help us
        await wait(1000)
      }
      return null
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
